import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Scissors, Loader2, Mail } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const HERO = "https://images.pexels.com/photos/9992819/pexels-photo-9992819.jpeg";

function Shell({ title, subtitle, children }) {
  return (
    <div className="grid min-h-screen lg:grid-cols-2 bg-[#0A0A0A]">
      <div className="relative hidden lg:block">
        <img src={HERO} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/20" />
        <Link to="/" className="absolute top-8 left-8 flex items-center gap-2">
          <Scissors className="h-6 w-6 text-[#DCA846]" />
          <span className="font-display text-xl font-bold uppercase tracking-wider">Epic Fade Academy</span>
        </Link>
        <div className="absolute bottom-12 left-8 max-w-sm">
          <p className="font-display text-3xl font-bold uppercase leading-tight">Master the craft. Own the chair.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <Link to="/" className="mb-8 flex items-center gap-2 lg:hidden">
            <Scissors className="h-6 w-6 text-[#DCA846]" />
            <span className="font-display text-lg font-bold uppercase">Epic Fade Academy</span>
          </Link>
          <h1 className="font-display text-3xl font-bold uppercase tracking-tight">{title}</h1>
          <p className="mt-2 text-sm text-zinc-400">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

function GoogleButton() {
  const onClick = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button data-testid="google-login-btn" type="button" variant="outline" onClick={onClick}
      className="mt-4 w-full border-white/15 bg-transparent text-white hover:bg-white/10">
      <Mail className="mr-2 h-4 w-4" /> Continue with Google
    </Button>
  );
}

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      nav(loc.state?.from || (u?.role === "admin" ? "/admin" : "/my-learning"));
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <Shell title="Welcome back" subtitle="Log in to continue learning.">
      <form onSubmit={submit} className="mt-8 space-y-4">
        <div><Label className="text-zinc-300">Email</Label>
          <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 bg-zinc-900 border-white/10 text-white" /></div>
        <div><Label className="text-zinc-300">Password</Label>
          <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1 bg-zinc-900 border-white/10 text-white" /></div>
        <Link to="/forgot-password" className="block text-right text-xs text-[#DCA846]">Forgot password?</Link>
        <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full bg-[#DCA846] py-6 text-black hover:bg-[#C5963B] font-display uppercase tracking-wide">
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Log in"}
        </Button>
      </form>
      <GoogleButton />
      <p className="mt-6 text-center text-sm text-zinc-400">No account? <Link to="/register" className="text-[#DCA846]">Sign up</Link></p>
    </Shell>
  );
}

export function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form.email, form.password, form.name);
      toast.success("Account created!");
      nav("/my-learning");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Registration failed");
    } finally { setLoading(false); }
  };

  return (
    <Shell title="Create account" subtitle="Join Epic Fade Academy today.">
      <form onSubmit={submit} className="mt-8 space-y-4">
        <div><Label className="text-zinc-300">Name</Label>
          <Input data-testid="register-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1 bg-zinc-900 border-white/10 text-white" /></div>
        <div><Label className="text-zinc-300">Email</Label>
          <Input data-testid="register-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1 bg-zinc-900 border-white/10 text-white" /></div>
        <div><Label className="text-zinc-300">Password</Label>
          <Input data-testid="register-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} className="mt-1 bg-zinc-900 border-white/10 text-white" /></div>
        <Button data-testid="register-submit" type="submit" disabled={loading} className="w-full bg-[#DCA846] py-6 text-black hover:bg-[#C5963B] font-display uppercase tracking-wide">
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Create account"}
        </Button>
      </form>
      <GoogleButton />
      <p className="mt-6 text-center text-sm text-zinc-400">Already have an account? <Link to="/login" className="text-[#DCA846]">Log in</Link></p>
    </Shell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const api = (await import("@/lib/api")).default;
    await api.post("/auth/forgot-password", { email });
    setSent(true);
  };
  return (
    <Shell title="Reset password" subtitle="We'll email you a reset link.">
      {sent ? <p className="mt-8 text-emerald-400">If an account exists, a reset link has been sent.</p> : (
        <form onSubmit={submit} className="mt-8 space-y-4">
          <Input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="Email" className="bg-zinc-900 border-white/10 text-white" />
          <Button data-testid="forgot-submit" type="submit" className="w-full bg-[#DCA846] py-6 text-black font-display uppercase">Send reset link</Button>
        </form>
      )}
      <p className="mt-6 text-center text-sm"><Link to="/login" className="text-[#DCA846]">Back to login</Link></p>
    </Shell>
  );
}

export function ResetPassword() {
  const nav = useNavigate();
  const token = new URLSearchParams(useLocation().search).get("token");
  const [password, setPassword] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    const api = (await import("@/lib/api")).default;
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated. Please log in.");
      nav("/login");
    } catch (err) { toast.error(err.response?.data?.detail || "Reset failed"); }
  };
  return (
    <Shell title="New password" subtitle="Choose a new password.">
      <form onSubmit={submit} className="mt-8 space-y-4">
        <Input data-testid="reset-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} placeholder="New password" className="bg-zinc-900 border-white/10 text-white" />
        <Button data-testid="reset-submit" type="submit" className="w-full bg-[#DCA846] py-6 text-black font-display uppercase">Update password</Button>
      </form>
    </Shell>
  );
}

export function AuthCallback() {
  const nav = useNavigate();
  const { googleSession } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sid = new URLSearchParams(window.location.hash.replace("#", "")).get("session_id");
    if (!sid) { nav("/login"); return; }
    googleSession(sid)
      .then((u) => nav(u?.role === "admin" ? "/admin" : "/my-learning", { replace: true }))
      .catch(() => nav("/login"));
  }, [googleSession, nav]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-black">
      <Loader2 className="h-8 w-8 animate-spin text-[#DCA846]" />
    </div>
  );
}
